import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Activity, AlertTriangle, AlertOctagon, Info, CheckCircle2,
  Loader2, RefreshCw, Sparkles, ExternalLink, Plus, Trash2, X, ShieldAlert,
  Filter, ChevronDown, ClipboardList,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import SurveillanceDefensivePanel from "@/components/SurveillanceDefensivePanel";
import RegistryScrapingPanel from "@/components/RegistryScrapingPanel";

const CRIT_STYLE = {
  critical: { icon: AlertOctagon, color: "#E24B4A", label: "Critique" },
  high: { icon: AlertTriangle, color: "#EF9F27", label: "Élevée" },
  medium: { icon: Info, color: "#378ADD", label: "Moyenne" },
  low: { icon: CheckCircle2, color: "#1D9E75", label: "Faible" },
};

function CritBadge({ value }) {
  if (!value) {
    return <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-400">non analysé</span>;
  }
  const m = CRIT_STYLE[value] || CRIT_STYLE.low;
  const Icon = m.icon;
  return (
    <span className="text-[10px] px-2 py-0.5 rounded inline-flex items-center gap-1"
      style={{ background: `${m.color}1A`, color: m.color, border: `1px solid ${m.color}40` }}>
      <Icon className="w-2.5 h-2.5" /> {m.label}
    </span>
  );
}

function LevelBadge({ level, isConstanteSignal }) {
  if (isConstanteSignal) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded inline-flex items-center gap-1 bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40 font-semibold uppercase tracking-wider">
        Signal Constante
      </span>
    );
  }
  if (level === "platform") {
    return <span className="text-[10px] px-2 py-0.5 rounded bg-[#378ADD]/15 text-[#378ADD] border border-[#378ADD]/30 uppercase tracking-wider">Plateforme</span>;
  }
  if (level === "workspace") {
    return <span className="text-[10px] px-2 py-0.5 rounded bg-[#7F77DD]/15 text-[#7F77DD] border border-[#7F77DD]/30 uppercase tracking-wider">Workspace</span>;
  }
  return null;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso || "—"; }
}

export default function Trend({ embedded = false }) {
  const { t } = useTranslation();
  const [signals, setSignals] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ criticality: "", category: "" });
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [showSources, setShowSources] = useState(false);

  const load = async () => {
    try {
      const [s1, s2] = await Promise.all([
        api.get("/workspace/trend/signals?limit=120"),
        api.get("/workspace/trend/sources"),
      ]);
      setSignals(s1.data.items || []);
      setSources(s2.data.items || []);
    } catch (e) { setError(fmtError(e)); setSignals([]); }
  };
  useEffect(() => { load(); }, []);

  const refreshSource = async (s) => {
    setRefreshing(true);
    try {
      if (s.level === "workspace") {
        const r = await api.post(`/workspace/trend/sources/${s.id}/refresh`);
        notify.success(`${r.data.inserted} nouveau(x) signal(aux) ingéré(s) depuis ${s.name}.`);
        await load();
      } else {
        notify.info("Sources plateforme : rafraîchissement géré par l'Ultra Admin.");
      }
    } catch (e) { notify.error(fmtError(e)); }
    finally { setRefreshing(false); }
  };

  const analyze = async (sig) => {
    setAnalyzing(sig.id);
    try {
      const r = await api.post(`/workspace/trend/signals/${sig.id}/analyze`);
      setSignals((arr) => arr.map((s) => (s.id === sig.id ? r.data : s)));
      notify.success("Signal analysé par NOAM.");
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAnalyzing(null); }
  };

  const convertToRisk = async (sig) => {
    if (!window.confirm(`Créer un risque à partir de « ${sig.title.slice(0, 80)} » ?`)) return;
    try {
      const r = await api.post(`/workspace/trend/signals/${sig.id}/convert-to-risque`);
      notify.success(`Risque créé : ${r.data.code} — ${r.data.title.slice(0, 50)}`);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const [planningId, setPlanningId] = useState(null);
  const convertToPlan = async (sig) => {
    setPlanningId(sig.id);
    try {
      const r = await api.post(`/workspace/trend/signals/${sig.id}/to-plan`);
      if (r.data.already_existed) {
        notify.info("Un plan d'action existe déjà pour cette actualité.");
      } else if (r.data.ai_generated) {
        notify.success(`Plan d'action créé avec 3 actions proposées par l'IA (${r.data.priorite}, échéance ${r.data.echeance}) — visible dans Plans d'action.`);
      } else {
        notify.success(`Plan d'action créé (${r.data.priorite}, échéance ${r.data.echeance}) — visible dans Plans d'action.`);
      }
    } catch (e) { notify.error(fmtError(e)); }
    finally { setPlanningId(null); }
  };

  const filtered = (signals || []).filter((s) => {
    if (filter.criticality && s.criticality !== filter.criticality) return false;
    if (filter.category && s.category !== filter.category) return false;
    return true;
  });

  const categories = [...new Set((signals || []).map((s) => s.category).filter(Boolean))].sort();

  return (
    <div className="space-y-6" data-testid="trend-root">
      {embedded ? (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-zinc-400 text-xs max-w-3xl">
            Flux RSS/Atom permanents analysés par l'IA (criticité, impacts, risques). Le Radar scanne — la Veille écoute.
          </p>
          <div className="flex items-center gap-2">
            <ModuleHelpButton moduleKey="trend" />
            <button onClick={() => setShowSources((v) => !v)} className="btn-ghost text-xs" data-testid="trend-toggle-sources">
              <Activity className="w-3.5 h-3.5" /> {t("saas.trend.sources_btn")} ({sources.length})
              <ChevronDown className={`w-3 h-3 transition-transform ${showSources ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
      ) : (
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
            <Activity className="w-3 h-3" /> {t("saas.trend.overline")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.trend.title")}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            {t("saas.trend.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModuleHelpButton moduleKey="trend" />
          <button onClick={() => setShowSources((v) => !v)} className="btn-ghost text-xs" data-testid="trend-toggle-sources">
            <Activity className="w-3.5 h-3.5" /> {t("saas.trend.sources_btn")} ({sources.length})
            <ChevronDown className={`w-3 h-3 transition-transform ${showSources ? "rotate-180" : ""}`} />
          </button>
        </div>
      </header>
      )}

      {/* V23.15.35 — Doctrine 3Q · tendance émergente à investiguer */}
      <ThreeQPageHeader endpoint="/workspace/trend/top-priority" />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {showSources && (
        <SourcesPanel sources={sources} refreshing={refreshing} onRefresh={refreshSource} onReload={load} />
      )}

      {/* P0 Palantir — Recherche hybride dans Trend */}
      <HybridResearchPanel
        contextModule="trend"
        defaultMode="hybrid"
        title="Veille hybride — interne + web"
        testid="trend-hybrid-research"
      />

      <div className="noam-card p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-xs text-zinc-400">Filtrer :</span>
          <select value={filter.criticality} onChange={(e) => setFilter({ ...filter, criticality: e.target.value })}
            className="text-xs bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="trend-filter-criticality">
            <option value="">Toutes criticités</option>
            <option value="critical">Critique</option>
            <option value="high">Élevée</option>
            <option value="medium">Moyenne</option>
            <option value="low">Faible</option>
          </select>
          <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="text-xs bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="trend-filter-category">
            <option value="">Toutes catégories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-[11px] text-zinc-500 ml-auto">{filtered.length} signal{filtered.length > 1 ? "aux" : ""}</span>
        </div>
      </div>

      {signals === null && (
        <div className="noam-card p-10 text-center text-zinc-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}
      {signals && filtered.length === 0 && (
        <div className="noam-card p-10 text-center" data-testid="trend-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
            <Activity className="w-6 h-6 text-[#378ADD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.trend.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.trend.empty_body")}
          </p>
        </div>
      )}

      <div className="space-y-3" data-testid="trend-signals-list">
        {filtered.map((sig) => (
          <SignalCard
            key={sig.id}
            sig={sig}
            analyzing={analyzing === sig.id}
            planning={planningId === sig.id}
            onAnalyze={() => analyze(sig)}
            onConvertToRisk={() => convertToRisk(sig)}
            onConvertToPlan={() => convertToPlan(sig)}
          />
        ))}
      </div>

      {/* V23.6 — Surveillance défensive (Tavily-only, gratuit) */}
      <SurveillanceDefensivePanel />

      {/* V23.7 — Scraping multi-registres gratuit (Zefix CH / Companies House UK / SEC EDGAR US / EUR-Lex EU) */}
      <RegistryScrapingPanel />
    </div>
  );
}

function SignalCard({ sig, analyzing, planning, onAnalyze, onConvertToRisk, onConvertToPlan }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="noam-card p-5" data-testid={`trend-signal-${sig.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-zinc-400 font-medium">{sig.source_name}</span>
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-500">{fmtDate(sig.published_at)}</span>
            <CritBadge value={sig.criticality} />
            {sig.category && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">{sig.category}</span>}
            <LevelBadge level={sig.level} isConstanteSignal={sig.is_constante_signal} />
          </div>
          <h3 className="text-sm text-white font-medium mt-2 leading-snug">{sig.title}</h3>
          {sig.summary && (
            <p className={`text-xs text-zinc-400 mt-2 leading-relaxed ${expanded ? "" : "line-clamp-2"}`}>{sig.summary}</p>
          )}
          {sig.summary && sig.summary.length > 200 && (
            <button onClick={() => setExpanded((v) => !v)} className="text-[11px] text-[#378ADD] mt-1">
              {expanded ? "Réduire" : "Lire la suite"}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {sig.url && (
            <a href={sig.url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5" title="Source originale">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {!sig.analyzed && (
            <button onClick={onAnalyze} disabled={analyzing}
              className="btn-primary text-[11px] py-1.5 px-2.5 disabled:opacity-50"
              data-testid={`trend-analyze-${sig.id}`}>
              {analyzing ? <><Loader2 className="w-3 h-3 animate-spin" /> Analyse…</> : <><Sparkles className="w-3 h-3" /> Analyser</>}
            </button>
          )}
          {sig.analyzed && (
            <button onClick={onConvertToRisk}
              className="btn-ghost text-[11px] py-1.5 px-2.5 hover:text-[#E24B4A]"
              title="Créer un risque dans le module Risques"
              data-testid={`trend-to-risk-${sig.id}`}>
              <ShieldAlert className="w-3 h-3" /> Risque
            </button>
          )}
          <button onClick={onConvertToPlan} disabled={planning}
            className="btn-ghost text-[11px] py-1.5 px-2.5 hover:text-[#1D9E75] disabled:opacity-50"
            title="Créer un plan d'action à partir de cette actualité"
            data-testid={`trend-to-plan-${sig.id}`}>
            {planning ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardList className="w-3 h-3" />} → Plan d&apos;action
          </button>
        </div>
      </div>
      {sig.analyzed && (sig.impacts?.length || sig.opportunities?.length || sig.risques?.length) && (
        <div className="grid md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/5">
          <AnalysisColumn label="Impacts" items={sig.impacts} color="#378ADD" />
          <AnalysisColumn label="Opportunités" items={sig.opportunities} color="#1D9E75" />
          <AnalysisColumn label="Risques" items={sig.risques} color="#E24B4A" />
        </div>
      )}
    </div>
  );
}

function AnalysisColumn({ label, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5" style={{ color }}>{label}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-zinc-200 flex items-start gap-1.5 leading-relaxed">
            <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourcesPanel({ sources, refreshing, onRefresh, onReload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [catalog, setCatalog] = useState({ sectors: [], langs: [] });
  const [quota, setQuota] = useState(null);
  const [filter, setFilter] = useState({ sector: "", lang: "", level: "" });
  const [busyToggleId, setBusyToggleId] = useState(null);

  useEffect(() => {
    api.get("/workspace/trend/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/workspace/trend/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, [sources]);

  const remove = async (s) => {
    if (s.level !== "workspace") {
      notify.info("Source plateforme — utilisez le toggle pour la désactiver pour votre workspace.");
      return;
    }
    if (!window.confirm(`Supprimer la source « ${s.name} » ?`)) return;
    try {
      await api.delete(`/workspace/trend/sources/${s.id}`);
      notify.success("Source supprimée.");
      await onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const toggleSource = async (s) => {
    setBusyToggleId(s.id);
    try {
      const nextActive = !s.org_active;
      if (s.level === "platform") {
        await api.put(`/workspace/trend/sources/${s.id}/state`, { active: nextActive });
      } else {
        await api.patch(`/workspace/trend/sources/${s.id}/active`, { active: nextActive });
      }
      notify.success(nextActive ? `${s.name} activée pour votre workspace.` : `${s.name} désactivée.`);
      await onReload();
    } catch (e) { notify.error(fmtError(e)); }
    setBusyToggleId(null);
  };

  const filtered = sources.filter((s) => {
    if (filter.sector && !(s.sectors || []).includes(filter.sector)) return false;
    if (filter.lang && s.lang !== filter.lang && s.lang !== "multi") return false;
    if (filter.level && s.level !== filter.level) return false;
    return true;
  });

  // Group by sector for clarity
  const groupedBySector = {};
  filtered.forEach((s) => {
    const sec = (s.sectors || [])[0] || "Autres";
    if (!groupedBySector[sec]) groupedBySector[sec] = [];
    groupedBySector[sec].push(s);
  });
  const sortedSectors = Object.keys(groupedBySector).sort();

  return (
    <div className="noam-card p-5" data-testid="trend-sources-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="noam-overline">Gestionnaire de sources</div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Toggle ON/OFF pour activer ou désactiver chaque source dans <span className="text-white">votre workspace</span>.
            Les sources <span className="text-[#378ADD]">plateforme</span> sont curées par Constante Partners.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary text-xs" data-testid="trend-add-source">
          <Plus className="w-3.5 h-3.5" /> Ajouter une source
        </button>
      </div>

      {quota && (
        <div className="flex items-center gap-3 flex-wrap p-3 rounded-md bg-white/[0.02] border border-white/5 mb-3 text-xs" data-testid="trend-quota-bar">
          <span className="text-zinc-500">Plan</span>
          <span className="text-white font-semibold uppercase">{quota.plan}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-400">{quota.current_active} source(s) active(s){quota.max_sources != null ? ` / ${quota.max_sources}` : ""}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-400">{quota.current_sectors.length} secteur(s){quota.max_sectors != null ? ` / ${quota.max_sectors}` : ""}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-400">{quota.current_langs.length} langue(s){quota.max_langs != null ? ` / ${quota.max_langs}` : ""}</span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <select value={filter.sector} onChange={(e) => setFilter({ ...filter, sector: e.target.value })}
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="trend-source-filter-sector">
          <option value="">Tous secteurs</option>
          {catalog.sectors.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.lang} onChange={(e) => setFilter({ ...filter, lang: e.target.value })}
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="trend-source-filter-lang">
          <option value="">Toutes langues</option>
          {catalog.langs.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <select value={filter.level} onChange={(e) => setFilter({ ...filter, level: e.target.value })}
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="trend-source-filter-level">
          <option value="">Tous niveaux</option>
          <option value="platform">Plateforme</option>
          <option value="workspace">Workspace</option>
        </select>
        <span className="text-zinc-500 ml-auto">{filtered.length} source(s) affichée(s)</span>
      </div>

      <div className="space-y-4">
        {sortedSectors.map((sector) => (
          <div key={sector}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#378ADD] font-semibold mb-2">{sector}</div>
            <div className="space-y-1.5">
              {groupedBySector[sector].map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-md bg-white/[0.02] border border-white/5" data-testid={`trend-source-row-${s.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleSource(s)}
                    disabled={busyToggleId === s.id}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${s.org_active ? "bg-[#1D9E75]" : "bg-zinc-700"} disabled:opacity-50 shrink-0`}
                    title={s.org_active ? "Désactiver pour mon workspace" : "Activer pour mon workspace"}
                    data-testid={`trend-source-toggle-${s.id}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${s.org_active ? "translate-x-5" : "translate-x-1"}`} />
                  </button>
                  <LevelBadge level={s.level} />
                  {s.lang && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold bg-white/5 border border-white/10 text-zinc-400">{s.lang}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{s.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono truncate">{s.url}</div>
                  </div>
                  {s.last_fetched_at && <span className="text-[10px] text-zinc-500 hidden md:inline">Fetch : {fmtDate(s.last_fetched_at)}</span>}
                  <button onClick={() => onRefresh(s)} disabled={refreshing || s.level !== "workspace"}
                    className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-30"
                    title={s.level === "workspace" ? "Rafraîchir" : "Source plateforme (rafraîchissement Ultra Admin)"}>
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                  </button>
                  {s.level === "workspace" && (
                    <button onClick={() => remove(s)} className="p-1.5 rounded text-zinc-500 hover:text-[#E24B4A] hover:bg-white/5">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showAdd && <AddSourceModal catalog={catalog} onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await onReload(); }} />}
    </div>
  );
}

function AddSourceModal({ catalog, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", url: "", sectors: "", categories: "", lang: "fr" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/workspace/trend/sources", {
        name: form.name.trim(),
        url: form.url.trim(),
        type: "rss",
        level: "workspace",
        sectors: form.sectors.split(",").map((s) => s.trim()).filter(Boolean),
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
        lang: form.lang || "fr",
        active: true,
      });
      notify.success("Source ajoutée. Cliquez sur l'icône Rafraîchir pour récupérer les premiers signaux.");
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="trend-add-source-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">Ajouter une source RSS</div>
            <div className="noam-display text-base font-semibold mt-0.5">Veille workspace</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom *</label>
            <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : BCE · Communiqués" className="noam-input" data-testid="trend-source-name" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">URL RSS *</label>
            <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://..." className="noam-input font-mono text-xs" data-testid="trend-source-url" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Secteur</label>
              <select value={form.sectors} onChange={(e) => setForm({ ...form, sectors: e.target.value })}
                className="noam-input" data-testid="trend-source-sector">
                <option value="">— Choisir —</option>
                {(catalog?.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Langue</label>
              <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
                className="noam-input" data-testid="trend-source-lang">
                {(catalog?.langs || ["fr", "de", "en", "multi"]).map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Catégories (optionnel, séparées par virgule)</label>
            <input value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })}
              placeholder="Réglementaire, Europe" className="noam-input" />
          </div>
          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="trend-source-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Création…</> : <><Plus className="w-3.5 h-3.5" /> Ajouter la source</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
